import { ValidationError } from '~/errors';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type {
  CreateLeaseSubsidyDto,
  DeleteLeaseSubsidyDto,
  LeaseSubsidyDto,
  LeaseSubsidyPaginationDto,
  UpdateLeaseSubsidyDto,
} from '~/swagger/portal/Api';
import type { ApiFilter } from '~/types/apiFilters';

export function validateLeaseId(leaseId: string): void {
  if (!leaseId) throw new ValidationError('Lease ID must be provided');
}

export function validateLeaseSubsidyId(subsidyId: string): void {
  if (!subsidyId) throw new ValidationError('Lease Subsidy ID must be provided');
}

export function validateCreateLeaseSubsidy(subsidy: CreateLeaseSubsidyDto): void {
  if (!subsidy) throw new ValidationError('Lease Subsidy object must be provided');
}

export function validateUpdateLeaseSubsidy(subsidy: UpdateLeaseSubsidyDto): void {
  if (!subsidy) throw new ValidationError('Lease Subsidy object must be provided');
}

export function validateDeleteLeaseSubsidy(deleteDto: DeleteLeaseSubsidyDto): void {
  if (!deleteDto) throw new ValidationError('Delete Lease Subsidy object must be provided');
}

export type LeaseSubsidiesFilter = ApiFilter<{
  page?: number;
  pageSize?: number;
  search?: string;
  subsidyType?: string;
  active?: boolean;
}>;

export class LeaseSubsidiesRepository extends BaseRestRepository {
  public async getLeasesSubsidies(params: { leaseId: string }, filter?: LeaseSubsidiesFilter): Promise<LeaseSubsidyPaginationDto> {
    validateLeaseId(params.leaseId);
    const url = urlWithQuery(`/api/leases/${params.leaseId}/subsidies`, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async createLeaseSubsidy(params: { leaseId: string }, payload: CreateLeaseSubsidyDto): Promise<LeaseSubsidyDto> {
    validateLeaseId(params.leaseId);
    validateCreateLeaseSubsidy(payload);
    const url = `/api/leases/${params.leaseId}/subsidies`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async getLeaseSubsidy(params: { leaseId: string; subsidyId: string }): Promise<LeaseSubsidyDto> {
    validateLeaseId(params.leaseId);
    validateLeaseSubsidyId(params.subsidyId);
    const url = `/api/leases/${params.leaseId}/subsidies/${params.subsidyId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async updateLeaseSubsidy(
    params: { leaseId: string; subsidyId: string },
    payload: UpdateLeaseSubsidyDto
  ): Promise<LeaseSubsidyDto> {
    validateLeaseId(params.leaseId);
    validateLeaseSubsidyId(params.subsidyId);
    validateUpdateLeaseSubsidy(payload);
    const url = `/api/leases/${params.leaseId}/subsidies/${params.subsidyId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async deleteLeaseSubsidy(params: { leaseId: string; subsidyId: string }, payload: DeleteLeaseSubsidyDto): Promise<void> {
    validateLeaseId(params.leaseId);
    validateLeaseSubsidyId(params.subsidyId);
    const url = `/api/leases/${params.leaseId}/subsidies/${params.subsidyId}`;
    const requestOptions: RequestInit = {
      method: 'DELETE',
    };

    if (payload) {
      validateDeleteLeaseSubsidy(payload);
      requestOptions.body = JSON.stringify(payload);
    }

    await this.fetchWithAuth(url, requestOptions);
  }

  public async deactivateLeaseSubsidy(params: { leaseId: string; subsidyId: string }): Promise<LeaseSubsidyDto> {
    validateLeaseId(params.leaseId);
    validateLeaseSubsidyId(params.subsidyId);
    const url = `/api/leases/${params.leaseId}/subsidies/${params.subsidyId}/deactivate`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
    });
    return this.getJsonResponse(response);
  }

  public async reactivateLeaseSubsidy(params: { leaseId: string; subsidyId: string }): Promise<LeaseSubsidyDto> {
    validateLeaseId(params.leaseId);
    validateLeaseSubsidyId(params.subsidyId);
    const url = `/api/leases/${params.leaseId}/subsidies/${params.subsidyId}/reactivate`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
    });
    return this.getJsonResponse(response);
  }
}

export const leaseSubsidiesRepository = new LeaseSubsidiesRepository();
